import React, { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useAuth0 } from '@auth0/auth0-react'
import { useQuery } from '@tanstack/react-query'
import {
  ArrowLeftIcon,
  UsersIcon,
  BookOpenIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  TagIcon
} from '@heroicons/react/24/outline'
import { TaleFromTheRail, useRailbirdGamesApi } from '../../services/railbirdGamesApi'
import { isAdmin } from '../../utils/roles'
import CreateTaleModal from '../../components/CreateTaleModal'
import EditTaleModal from '../../components/EditTaleModal'
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal'
import toast from 'react-hot-toast'

export default function GameDetailPage() {
  const { gameId } = useParams<{ gameId: string }>()
  const navigate = useNavigate()
  const { user, getAccessTokenSilently } = useAuth0()
  const railbirdGamesApi = useRailbirdGamesApi()
  const [showCreateTaleModal, setShowCreateTaleModal] = useState(false)
  const [editingTale, setEditingTale] = useState<TaleFromTheRail | null>(null)
  const [deletingTale, setDeletingTale] = useState<TaleFromTheRail | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [selectedTags, setSelectedTags] = useState<string[]>([])

  const {
    data: game,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['railbird-game', gameId],
    queryFn: () => railbirdGamesApi.getGame(gameId!, getAccessTokenSilently),
    enabled: !!gameId && !!user
  })

  const userIsAdmin = isAdmin(user)

  // Get all unique tags from tales
  const allTags = Array.from(new Set(
    game?.talesFromTheRail.flatMap(tale => tale.tags) || []
  )).sort()

  // Filter tales by selected tags
  const filteredTales = game?.talesFromTheRail.filter(tale => 
    selectedTags.length === 0 || selectedTags.some(tag => tale.tags.includes(tag))
  ) || []

  const handleTagToggle = (tag: string) => {
    setSelectedTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    )
  }

  const handleCreateTaleSuccess = () => {
    setShowCreateTaleModal(false)
    refetch()
    toast.success('Tale added successfully!')
  }

  const handleEditTaleSuccess = () => {
    setEditingTale(null)
    refetch()
    toast.success('Tale updated successfully!')
  }

  const handleDeleteTale = (tale: TaleFromTheRail) => {
    setDeletingTale(tale)
  }

  const confirmDeleteTale = async () => {
    if (!deletingTale || !gameId) return

    try {
      setIsDeleting(true)
      await railbirdGamesApi.deleteTale(gameId, deletingTale.id)
      toast.success('Tale deleted successfully')
      setDeletingTale(null)
      refetch()
    } catch (error) {
      console.error('Delete error:', error)
      toast.error('Failed to delete tale')
    } finally {
      setIsDeleting(false)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    )
  }

  if (error || !game) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Game Not Found</h2>
          <button
            onClick={() => navigate('/legacy/railbird-games')}
            className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
          >
            Back to Games
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100 dark:from-gray-900 dark:to-gray-800">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8"
        >
          {/* Header */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/legacy/railbird-games')}
              className="flex items-center space-x-2 text-amber-700 hover:text-amber-800 dark:text-amber-300 dark:hover:text-amber-200 transition-colors"
              style={{ fontFamily: 'Georgia, serif' }}
            >
              <ArrowLeftIcon className="w-5 h-5" />
              <span>Back to Games</span>
            </button>
          </div>

          {/* Game Details Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white/90 dark:bg-gray-800/90 backdrop-blur rounded-lg shadow-xl border-2 border-amber-200 dark:border-amber-700 overflow-hidden"
          >
            {/* Game Header */}
            <div className="bg-gradient-to-r from-amber-600 to-orange-600 p-6 text-white">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Georgia, serif' }}>
                    {game.name}
                  </h1>
                  <div className="flex items-center space-x-6 text-amber-100">
                    <div className="flex items-center space-x-1">
                      <UsersIcon className="w-5 h-5" />
                      <span style={{ fontFamily: 'Georgia, serif' }}>
                        Max {game.maxPlayers} players
                      </span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <BookOpenIcon className="w-5 h-5" />
                      <span style={{ fontFamily: 'Georgia, serif' }}>
                        {game.talesFromTheRail.length} tale{game.talesFromTheRail.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                </div>
                {userIsAdmin && (
                  <button
                    onClick={() => navigate(`/legacy/railbird-games/${game.id}/edit`)}
                    className="flex items-center space-x-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                    style={{ fontFamily: 'Georgia, serif' }}
                  >
                    <PencilIcon className="w-5 h-5" />
                    <span>Edit Game</span>
                  </button>
                )}
              </div>
            </div>

            {/* Game Content */}
            <div className="p-6 space-y-6">
              {/* Game Photos */}
              {game.images && game.images.length > 0 && (
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3" style={{ fontFamily: 'Georgia, serif' }}>
                    Game Photos
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {game.images.map((image, index) => (
                      <div key={index} className="aspect-square rounded-lg overflow-hidden border-2 border-amber-200 dark:border-amber-700 shadow-md hover:shadow-lg transition-shadow">
                        <img
                          src={image}
                          alt={`${game.name} - Photo ${index + 1}`}
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Description */}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3" style={{ fontFamily: 'Georgia, serif' }}>
                  Description
                </h2>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed" style={{ fontFamily: 'Georgia, serif' }}>
                  {game.description}
                </p>
              </div>

              {/* Rules */}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3" style={{ fontFamily: 'Georgia, serif' }}>
                  Rules
                </h2>
                <div className="prose prose-amber dark:prose-invert max-w-none">
                  <div 
                    className="text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap"
                    style={{ fontFamily: 'Georgia, serif' }}
                  >
                    {game.rules}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Tales from the Rail Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white/90 dark:bg-gray-800/90 backdrop-blur rounded-lg shadow-xl border-2 border-amber-200 dark:border-amber-700 overflow-hidden"
          >
            {/* Tales Header */}
            <div className="bg-gradient-to-r from-amber-700 to-orange-700 p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Georgia, serif' }}>
                    Tales from the Rail
                  </h2>
                  <p className="text-amber-100 italic" style={{ fontFamily: 'Georgia, serif' }}>
                    "A place to share old family stories, poker lore, and colorful histories — 
                    a collection of tales and traditions that bring each game's rich past to life."
                  </p>
                </div>
                {userIsAdmin && (
                  <button
                    onClick={() => setShowCreateTaleModal(true)}
                    className="flex items-center space-x-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                    style={{ fontFamily: 'Georgia, serif' }}
                  >
                    <PlusIcon className="w-5 h-5" />
                    <span>Add Tale</span>
                  </button>
                )}
              </div>
            </div>

            {/* Tag Filter */}
            {allTags.length > 0 && (
              <div className="p-4 border-b border-amber-200 dark:border-amber-700">
                <div className="flex flex-wrap gap-2">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300 mr-2" style={{ fontFamily: 'Georgia, serif' }}>
                    Filter by tags:
                  </span>
                  {allTags.map(tag => (
                    <button
                      key={tag}
                      onClick={() => handleTagToggle(tag)}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                        selectedTags.includes(tag)
                          ? 'bg-amber-600 text-white'
                          : 'bg-amber-100 dark:bg-amber-900 text-amber-800 dark:text-amber-200 hover:bg-amber-200 dark:hover:bg-amber-800'
                      }`}
                      style={{ fontFamily: 'Georgia, serif' }}
                    >
                      {tag}
                    </button>
                  ))}
                  {selectedTags.length > 0 && (
                    <button
                      onClick={() => setSelectedTags([])}
                      className="px-3 py-1 rounded-full text-xs font-medium bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                      style={{ fontFamily: 'Georgia, serif' }}
                    >
                      Clear filters
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Tales List */}
            <div className="p-6">
              {filteredTales.length === 0 ? (
                <div className="text-center py-12">
                  <BookOpenIcon className="w-16 h-16 text-amber-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2" style={{ fontFamily: 'Georgia, serif' }}>
                    {selectedTags.length > 0 ? 'No tales match your filters' : 'No tales yet'}
                  </h3>
                  <p className="text-amber-700 dark:text-amber-300" style={{ fontFamily: 'Georgia, serif' }}>
                    {selectedTags.length > 0 
                      ? 'Try adjusting your tag filters to see more tales.'
                      : 'Be the first to share a tale about this game!'
                    }
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {filteredTales.map((tale, index) => (
                    <motion.div
                      key={tale.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 * index }}
                      className="border-l-4 border-amber-600 bg-amber-50/50 dark:bg-amber-900/20 p-6 rounded-r-lg shadow-md hover:shadow-lg transition-shadow"
                    >
                      {/* Tale Header */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2" style={{ fontFamily: 'Georgia, serif' }}>
                            {tale.title}
                          </h3>
                          <p className="text-sm text-amber-700 dark:text-amber-300" style={{ fontFamily: 'Georgia, serif' }}>
                            Scribed by <span className="font-semibold">{tale.scribe}</span> • {new Date(tale.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                        {userIsAdmin && (
                          <div className="flex items-center space-x-1 ml-4">
                            <button
                              onClick={() => setEditingTale(tale)}
                              className="p-1 text-amber-600 hover:text-amber-700 hover:bg-amber-100 dark:hover:bg-amber-900/50 rounded transition-colors"
                              title="Edit tale"
                            >
                              <PencilIcon className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteTale(tale)}
                              className="p-1 text-red-600 hover:text-red-700 hover:bg-red-100 dark:hover:bg-red-900/50 rounded transition-colors"
                              title="Delete tale"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Tale Body */}
                      <div className="prose prose-amber dark:prose-invert max-w-none mb-4">
                        <div 
                          className="text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap"
                          style={{ fontFamily: 'Georgia, serif' }}
                        >
                          {tale.body}
                        </div>
                      </div>

                      {/* Tale Photos */}
                      {tale.images && tale.images.length > 0 && (
                        <div className="mb-4">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {tale.images.map((image, imageIndex) => (
                              <div key={imageIndex} className="rounded-lg overflow-hidden border border-amber-300 dark:border-amber-600 shadow-sm hover:shadow-md transition-shadow">
                                <img
                                  src={image}
                                  alt={`${tale.title} - Photo ${imageIndex + 1}`}
                                  className="w-full h-32 sm:h-40 object-cover hover:scale-105 transition-transform duration-300"
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Tags */}
                      {tale.tags.length > 0 && (
                        <div className="flex items-center space-x-2">
                          <TagIcon className="w-4 h-4 text-amber-600" />
                          <div className="flex flex-wrap gap-1">
                            {tale.tags.map(tag => (
                              <span
                                key={tag}
                                className="px-2 py-1 bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-200 text-xs rounded-full"
                                style={{ fontFamily: 'Georgia, serif' }}
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>

        {/* Create Tale Modal */}
        <CreateTaleModal
          isOpen={showCreateTaleModal}
          onClose={() => setShowCreateTaleModal(false)}
          gameId={gameId!}
          onSuccess={handleCreateTaleSuccess}
        />

        {/* Edit Tale Modal */}
        <EditTaleModal
          isOpen={!!editingTale}
          onClose={() => setEditingTale(null)}
          gameId={gameId!}
          tale={editingTale}
          onSuccess={handleEditTaleSuccess}
        />

        {/* Delete Tale Confirmation Modal */}
        <ConfirmDeleteModal
          isOpen={!!deletingTale}
          onClose={() => setDeletingTale(null)}
          onConfirm={confirmDeleteTale}
          title="Delete Tale"
          message={`Are you sure you want to delete "${deletingTale?.title}"? This action cannot be undone.`}
          confirmText="Delete Tale"
          isDeleting={isDeleting}
          countdownSeconds={3}
        />
      </div>
    </div>
  )
}
